import { Link } from "react-router-dom";
import styled from "styled-components";
import { Mark, Selection } from "../@interfaces/interfaces";
import { useParams } from "react-router-dom";
import { useRef, useEffect } from "react";

const getStatusMeta = (mark: Mark) => {
  const totalFrames = mark.total_frames ?? mark.length;
  const labeledFrames = mark.labeled_frames ?? totalFrames - mark.empty_count;
  const unlabeledFrames = mark.unlabeled_frames ?? totalFrames - labeledFrames;

  const derivedStatus = mark.marked
    ? labeledFrames === 0
      ? "reviewed_empty"
      : labeledFrames >= totalFrames
        ? "reviewed_complete"
        : "reviewed_partial"
    : "untouched";
  const status = mark.annotation_status ?? derivedStatus;

  if (status === "reviewed_complete") {
    return {
      label: "Reviewed Complete",
      className: "mark--status mark--status--complete",
      title: `${labeledFrames} labeled frames`,
    };
  }

  if (status === "reviewed_empty") {
    return {
      label: "Reviewed Empty",
      className: "mark--status mark--status--empty",
      title: `${unlabeledFrames} unlabeled frames`,
    };
  }

  if (status === "untouched") {
    return {
      label: "Untouched",
      className: "mark--status mark--status--untouched",
      title: "Not reviewed yet",
    };
  }

  return {
    label: `${labeledFrames} labeled, ${unlabeledFrames} unlabeled`,
    className: "mark--status mark--status--partial",
    title: `${labeledFrames} labeled, ${unlabeledFrames} unlabeled`,
  };
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid #d5dde3;
  margin-top: 1em;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafb;

  .no-shrink {
    flex-shrink: 0;
  }

  .title--box {
    background: linear-gradient(135deg, #2f6178, #417690);
    color: white;
    padding: 0.5em;
    margin-bottom: 0em;
    font-weight: 600;
  }
`;

const ScrollContent = styled.div`
  align-items: center;
  box-sizing: border-box;
  grid-template-rows: repeat(auto-fill);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  padding: 0.35rem 0;

  .mark {
    min-height: 20px;
    display: flex;
    gap: 0.5em;
    align-items: center;
    padding: 0.45rem 0.75rem;
    border-bottom: 1px solid #e7edf2;
    background: white;
  }

  .mark--disabled {
    background-color: #eef2f5;
    color: white;
    border-radius: 999px;
    font-size: 0.8rem;
    height: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.15rem 0.45rem;
    align-self: center;
  }

  .mark--empty {
    background-color: #f2f4f7;
    color: #5f6b76;
    border: 1px solid #d8dee5;
    border-radius: 999px;
    font-size: 0.8rem;
    width: fit-content;
    height: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.15rem 0.55rem;
    align-self: center;
  }

  .mark--marked {
    background-color: #1f7a4d;
    color: white;
    border-radius: 999px;
    width: fit-content;
    font-size: 0.8rem;
    height: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.15rem 0.55rem;
    align-self: center;
  }

  .mark--status {
    border-radius: 999px;
    width: fit-content;
    font-size: 0.75rem;
    font-weight: 600;
    height: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.18rem 0.6rem;
    align-self: center;
    letter-spacing: 0.01em;
  }

  .mark--status--complete {
    background: #d9f0e2;
    color: #17613a;
  }

  .mark--status--partial {
    background: #e9f1f8;
    color: #295d7a;
  }

  .mark--status--empty {
    background: #edf2f6;
    color: #5c6772;
  }

  .mark--status--untouched {
    background: #f3eefc;
    color: #5a4b85;
  }

  .mark--current {
    a {
      background-color: #2f6178;
      color: white;
      border-radius: 999px;
    }
  }
  .link {
    padding: 0.2rem 0.45rem;
  }

  .link--reset {
    color: inherit;
    text-decoration: none;
  }
  .length {
    font-size: 0.8em;
    font-weight: 200;
    display: flex;
    margin-left: auto;
    justify-content: center;
    align-items: center;
  }
  .eye--icon {
    width: 20px;
    opacity: 1;
  }
`;

interface SequenceNavProps {
  markList: Mark[];
  selections: Selection[];
}

function SequenceNav({ markList, selections }: SequenceNavProps) {
  const { frame } = useParams<{ frame: string }>();
  const { sequence_name } = useParams<{ sequence_name: string }>();

  const scrollRef = useRef<HTMLDivElement>(null);

  const sortedMarkList = markList.sort((a, b) => {
    const aNumber = parseInt(a.name.split("-frame")[1], 10);
    const bNumber = parseInt(b.name.split("-frame")[1], 10);
    return aNumber - bNumber;
  });

  useEffect(() => {
    // scroll to current frame
    const currentFrame = document.querySelector(".mark--current");
    if (currentFrame && scrollRef.current) {
      currentFrame.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <Wrapper>
      <div className="no-shrink title--box">{sequence_name}</div>
      <ScrollContent ref={scrollRef} id="sequence_nav">
        {sortedMarkList.map((mark, i) => {
          return (
            <div
              className={`mark ${mark.marked ? "" : ""} ${
                frame === mark.name ? "mark--current" : ""
              }`}
              key={i}
            >
              <Link
                className="link link--reset"
                to={`/${sequence_name}/${mark.name}`}
              >
                {mark.name.replace("image", "sequence").split("-")[0]}
              </Link>
              <span
                className={getStatusMeta(mark).className}
                title={getStatusMeta(mark).title}
              >
                {getStatusMeta(mark).label}
              </span>

              <div className="length">
                {mark.total_frames ?? mark.length} frames
              </div>
            </div>
          );
        })}
      </ScrollContent>
    </Wrapper>
  );
}

export default SequenceNav;
